import React, { useEffect, useMemo, useState } from 'react';
import { useElementSize } from 'usehooks-ts';
import styled, { ThemeProvider } from 'styled-components';
import { DateTime, Duration } from 'luxon';
import { CloseOutlined, ExclamationOutlined, QuestionOutlined } from '@ant-design/icons';

import { useMDSContext } from '@metservice/use-mds/src/use-mds';

import { WeatherWidgetRenderProps } from '@mono/types/src/weather-widgets';
import { CfjsonRow, ThreatMatrixSettings } from '@mono/types/src/weather-widgets/threat-matrix';
import { toHash } from '@mono/to-hash';
import { themeSelector } from '@mono/metui/src/utils/themeSelector';
import { useCdsTaf } from '@mono/use-cds-taf';

import { DayRow, HourRow, generateDaysWithHours } from './time-step';
import { CfjsonRowHeader, generateCellsForCfjson, generateObsCellForCfjson } from './cfjson-render';
import { Cell } from './types';
import { TafRowHeader, generateCellsForTaf } from './taf-render';

export const ThreatMatrix = (props: WeatherWidgetRenderProps<ThreatMatrixSettings>) => {
    const { settings: inputSettings, cfjsons: cfJsonsResponses, timezone, updateInterval } = props;
    const { capabilities } = useMDSContext();
    const [ref, { width }] = useElementSize();

    const [now, setNow] = useState(DateTime.utc().startOf('hour').toISO());

    const settings = useMemo(() => {
        if (inputSettings.rows.every((r) => r.type !== undefined)) return inputSettings;
        return {
            ...inputSettings,
            rows: inputSettings.rows.map((row) => {
                if (row.type) return row;
                return {
                    ...row,
                    type: 'cfjson',
                } as CfjsonRow;
            }),
        };
    }, [toHash(inputSettings)]);

    const icaoIdsFromSettings = useMemo(() => {
        return settings.rows
            .filter((r) => r.type === 'taf')
            .map((r: any) => r.icaoId)
            .filter((r) => !!r);
    }, [toHash(settings.rows)]);

    const rerenderOnHour = async () => {
        const now = DateTime.utc();
        const nextHour = now.plus({ hours: 1 }).startOf('hour').diff(now).as('milliseconds');
        setTimeout(() => {
            setNow(DateTime.utc().startOf('hour').toISO());
            rerenderOnHour();
        }, nextHour);
    };

    useEffect(() => {
        rerenderOnHour();
    }, []);

    const cdsTafHook = useCdsTaf({
        loadIcaoIds: icaoIdsFromSettings,
        updateInterval,
    });

    const showObs = useMemo(() => {
        return settings.rows.some((a) => {
            if (a.type === 'taf') {
                return true;
            }
            return a.obs && a.obs.capabilityId;
        });
    }, [toHash(settings.rows)]);

    const { rowsWithCells, daysWithHours } = useMemo(() => {
        const rowsWithCells = settings.rows.map((row) => {
            if (row.type === 'taf') {
                const cells = generateCellsForTaf({ cdsTafData: cdsTafHook.data, row, timeRange: settings.timeRange });
                return {
                    row,
                    cells,
                };
            }

            const cells = generateCellsForCfjson({ now, timeRange: settings.timeRange, capabilities, row, cfJsonsResponses });
            if (!showObs) return { row, cells };

            const obsCell = generateObsCellForCfjson({ row, capabilities, cfJsonsResponses });
            return { row, cells: [obsCell, ...cells] };
        });
        const daysWithHours = generateDaysWithHours(settings.timeRange, timezone);

        return { rowsWithCells, daysWithHours };
    }, [toHash(cfJsonsResponses), toHash(settings.rows), settings.timeRange, timezone, showObs, toHash(cdsTafHook.data), now]);

    const numberOfHours = Duration.fromISO(settings.timeRange.split('--')[1]).as('hours');

    const widthOfLabelDiv = settings.mode === 'cozy' ? 432 : 355;
    const widthOfRow = width - 12 - widthOfLabelDiv - (showObs ? 46 : 0);
    const cellWidth = Math.max(widthOfRow / numberOfHours, 24);

    return (
        <ThemeProvider theme={themeSelector(props.settings.theme)}>
            <ThreatMatrixStyled ref={ref} className='threat-matrix'>
                {/* Header */}
                {settings.title && (
                    <Header className='header'>
                        <h2>{settings.title}</h2>
                    </Header>
                )}
                {/* Time line for days and hours */}
                <DataContainer className='data-container'>
                    <DayRow daysWithHours={daysWithHours} showObs={showObs} cellWidth={cellWidth} mode={settings.mode} whiteSpaceWidth={widthOfLabelDiv + 12} />
                    <div style={{ height: 6 }} /> {/* Spacer */}
                    <HourRow
                        daysWithHours={daysWithHours}
                        showObs={showObs}
                        cellWidth={cellWidth}
                        mode={settings.mode}
                        whiteSpaceWidth={widthOfLabelDiv + 12}
                    />
                    <div style={{ height: 16 }} /> {/* Spacer */}
                    {/* Container for both labels and data */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexGrow: 1 }}>
                        {rowsWithCells.map(({ row, cells }, index) => {
                            return (
                                <Row key={index}>
                                    <DetailsDiv mode={settings.mode} width={widthOfLabelDiv}>
                                        {row.type === 'taf' && <TafRowHeader row={row} mode={settings.mode} />}
                                        {row.type === 'cfjson' && <CfjsonRowHeader row={row} mode={settings.mode} />}
                                    </DetailsDiv>

                                    {/* Row cells */}
                                    <div style={{ display: 'flex' }}>
                                        {cells.map((cell, index) => {
                                            return (
                                                <div key={index} style={{ paddingRight: cell.isObs ? 8 : 2, width: cell.isObs ? 44 : cellWidth * cell.width }}>
                                                    <Cell key={index} status={cell.status}>
                                                        {cell.status === 'alert' && <CloseOutlined style={{ color: 'rgba(0,0,0,0.4)', fontSize: 'large' }} />}
                                                        {cell.status === 'warning' && (
                                                            <ExclamationOutlined style={{ color: 'rgba(0,0,0,0.4)', fontSize: 'large' }} />
                                                        )}
                                                        {!cell.status && <QuestionOutlined style={{ color: 'rgba(0,0,0,0.4)', fontSize: 'large' }} />}
                                                        {/* {cell.label ?? 'N/A'} */}
                                                    </Cell>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </Row>
                            );
                        })}
                    </div>
                </DataContainer>
            </ThreatMatrixStyled>
        </ThemeProvider>
    );
};

//Styled components

const ThreatMatrixStyled = styled.div`
    display: flex;
    height: 100%;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    background-color: ${(props) => props.theme.background};

    h1,
    p,
    span {
        color: ${(props) => props.theme.text};
        white-space: nowrap;
    }
    box-shadow: 0px 2px 6px rgba(85, 91, 108, 0.3);
    border-radius: 4px;
`;

const Header = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    align-self: baseline;
    padding: 12px;
    position: sticky;
    height: 22px;
    padding-bottom: 0;
    h2 {
        font-family: 'Open Sans';
        font-style: normal;
        font-weight: 700;
        margin-bottom: 0;
        font-size: 16px;
        color: ${(props) => props.theme.text};
    }
`;

const DataContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    background: ${(props) => props.theme.background};
    overflow-x: auto;
    width: 100%;
    height: 100%;
    padding-bottom: 12px;
`;

const Row = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    flex-grow: 1;
    padding-left: 12px;
`;

const DetailsDiv = styled.div<{ mode?: 'cozy' | 'compact'; width: number }>`
    display: flex;
    position: sticky;
    align-items: center;
    z-index: 1;
    left: 0;
    min-width: ${(props) => props.width}px;
    max-width: ${(props) => props.width}px;
    background: ${(props) => props.theme.background};
    gap: 8px;
    padding-right: 16px;
`;

const Cell = styled.div<Omit<Cell, 'width'>>`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    background: ${(props) => {
        if (props.status === 'good') return '#74A30F';
        if (props.status === 'warning') return '#DF7B01';
        if (props.status === 'alert') return '#FA6161';
        return '#CDCDCD';
    }};
    border-radius: 4px;
`;
